// Payment API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export type PaymentMethod = 'stripe' | 'paypal' | 'bank_transfer' | 'credit_card';

export interface PaymentChannel {
  channelId: string;
  channelName: string;
  tier: number;
  price: number;
  status: 'pending' | 'approved' | 'rejected' | 'published';
  publishedUrl?: string;
  publishedAt?: string;
}

export interface PaymentHistoryPayment {
  _id: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  paymentMethod: Exclude<PaymentMethod, 'credit_card'>;
  transactionId?: string;
  stripePaymentIntentId?: string;
  stripeSessionId?: string;
  // Package & credits info
  packageType?: 'package' | 'standalone' | 'custom';
  packageTier?: string;
  packageCredits?: number;
  customPackageTierPRCounts?: { [tier: string]: number };
  stripeLiveMode?: boolean;
  channels: PaymentChannel[];
  totalChannels: number;
  createdAt: string;
  updatedAt: string;
  paidAt?: string;
  refundedAt?: string;
  refundReason?: string;
  refundAmount?: number;
  prId?: {
    _id: string;
    title?: string;
    status?: string;
  };
}

export interface PaymentHistoryPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PaymentHistoryResponse {
  success: boolean;
  payments: PaymentHistoryPayment[];
  pagination: PaymentHistoryPagination;
}

export interface RefundResponse {
  success: boolean;
  data?: any;
  message?: string;
}

export interface UserCreditsPackage {
  tier: string; // '1' | '2' | '3' | '4-5' | 'custom'
  credits: number;
  purchasedAt: string;
  tierPRCounts?: { [tier: string]: number };
}

export interface UserCreditsResponse {
  success: boolean;
  credits: {
    total: number;
    used: number;
    remaining: number;
  };
  tierCredits?: { [tier: string]: number }; // Tier-specific available credits
  packages: UserCreditsPackage[];
  hasActivePackage: boolean;
  hasRemainingCredits: boolean;
}

/**
 * Get payment history for the authenticated user
 */
export const getPaymentHistory = async (
  page = 1,
  limit = 50
): Promise<PaymentHistoryResponse> => {
  try {
    const response = await apiClient.get('/api/payments/history', {
      params: { page, limit }
    });

    return response.data as PaymentHistoryResponse;
  } catch (error: any) {
    console.error('Error fetching payment history:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch payment history');
  }
};

/**
 * Request a refund for a payment
 */
export const refundPayment = async (
  paymentId: string,
  options?: { amount?: number; reason?: string; notes?: string }
): Promise<RefundResponse> => {
  try {
    const response = await apiClient.post('/api/payments/refund', {
      paymentId,
      amount: options?.amount,
      reason: options?.reason,
      notes: options?.notes
    });
    return response.data as RefundResponse;
  } catch (error: any) {
    console.error('Error processing refund:', error);
    throw new Error(error.response?.data?.message || 'Failed to process refund');
  }
};

/**
 * Get user's PR credits and packages
 */
export const getUserCreditsApi = async (): Promise<UserCreditsResponse> => {
  try {
    const response = await apiClient.get('/api/payments/credits');
    return response.data as UserCreditsResponse;
  } catch (error: any) {
    console.error('Error fetching user credits:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch user credits');
  }
};

